import { Button, Card, Col, Row } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ArrowLeft from "../../../../../Theme/CustomIcons/Icons/Arrow/ArrowLeft";
import CompanyCards from "./components/CompanyCards";
import CreateNewEntityModal from "./components/CreateNewEntity";
import CommentsCard from "./components/CommentsCard";
import ActivityLogCard from "./components/ActivityLogCard";
import EntityDetailView from "./EntityDetailView";
import CustomLoaderIcon from "../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";
import MoreEmptyState from "../../../../../Theme/EmptyStates/MoreEmptyStates/MoreEmptyState";
import {
  getAllEntityListAction,
  getSingleEntityDetailsAction,
  getEntityCommentsAction,
  getEntityActivityLogsAction,
} from "../../../../../store/action/admin/entityActions";

const EntityParentLayout = () => {
  const dispatch = useDispatch();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const allEntityState = useSelector((state) => state.getAllEntityList);
  const singleEntityState = useSelector((state) => state.getSingleEntityList);

  const allEntities = useMemo(() => {
    const list = allEntityState?.info;
    return Array.isArray(list) ? list : [];
  }, [allEntityState?.info]);
  // Only show loading for entity list and details, not for comments/activity logs
  const loading = showDetails
    ? singleEntityState?.loading
    : allEntityState?.loading;

  useEffect(() => {
    // fetch all entities once on mount
    dispatch(getAllEntityListAction());
  }, [dispatch]);

  useEffect(() => {
    // set default selected id when list loads
    if (!selectedEntityId && allEntities.length > 0) {
      const firstId =
        allEntities[0]?._id || allEntities[0]?.id || allEntities[0]?.name;
      setSelectedEntityId(firstId);
    }
  }, [allEntities, selectedEntityId]);

  useEffect(() => {
    // fetch details when selected id changes
    if (selectedEntityId) {
      dispatch(getSingleEntityDetailsAction(selectedEntityId));
    }
  }, [dispatch, selectedEntityId]);

  const handleCreateEntity = (entityData) => {
    setIsCreateModalOpen(false);
    // Refresh the entity list after creating a new entity
    dispatch(getAllEntityListAction());
  };

  const handleOpenComments = (entityId) => {
    setSelectedEntityId(entityId);
    setCommentsOpen(true);
    // Refresh comments when opening comments drawer
    dispatch(getEntityCommentsAction(entityId));
  };

  const handleCloseComments = () => {
    setCommentsOpen(false);
  };

  const handleViewDetails = (entityId) => {
    setSelectedEntityId(entityId);
    setShowDetails(true);
    // Refresh comments and activity logs when viewing details
    dispatch(getEntityCommentsAction(entityId));
    dispatch(getEntityActivityLogsAction(entityId));
  };

  const currentIndex = useMemo(() => {
    return allEntities.findIndex(
      (e) => (e._id || e.id || e.name) === selectedEntityId
    );
  }, [allEntities, selectedEntityId]);

  const handleEntityChange = (newEntityId) => {
    setSelectedEntityId(newEntityId);
    dispatch(getEntityCommentsAction(newEntityId));
    dispatch(getEntityActivityLogsAction(newEntityId));
  };

  const handlePrev = () => {
    if (!allEntities.length) return;
    const idx = currentIndex <= 0 ? 0 : currentIndex - 1;
    const prevId =
      allEntities[idx]?._id || allEntities[idx]?.id || allEntities[idx]?.name;
    if (prevId) setSelectedEntityId(prevId);
    dispatch(getEntityCommentsAction(prevId));
    dispatch(getEntityActivityLogsAction(prevId));
  };

  const handleNext = () => {
    if (!allEntities.length) return;
    const idx =
      currentIndex >= allEntities.length - 1
        ? allEntities.length - 1
        : currentIndex + 1;
    const nextId =
      allEntities[idx]?._id || allEntities[idx]?.id || allEntities[idx]?.name;
    if (nextId) setSelectedEntityId(nextId);
    dispatch(getEntityCommentsAction(nextId));
    dispatch(getEntityActivityLogsAction(nextId));
  };

  const handleBackToList = () => {
    setShowDetails(false);
  };

  return (
    <Row>
      <Col span={24}>
        <Card
          title={showDetails && selectedEntityId ? null : "Entity"}
          extra={
            !showDetails && (
              <Button type="primary" onClick={() => setIsCreateModalOpen(true)}>
                Create New Entity
              </Button>
            )
          }
        >
          {loading && (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 16 }}
            >
              <CustomLoaderIcon />
            </div>
          )}
          {!loading &&
            (allEntities.length === 0 ? (
              <MoreEmptyState
                type="ErrorState"
                size="small"
                title="No entities found"
                text="Create your first entity to get started"
              />
            ) : showDetails && selectedEntityId ? (
              <EntityDetailView
                entityId={selectedEntityId}
                onBackToList={handleBackToList}
                allEntities={allEntities}
                onEntityChange={handleEntityChange}
                onPrev={handlePrev}
                onNext={handleNext}
                loading={loading}
              />
            ) : (
              <CompanyCards
                onOpenComments={handleOpenComments}
                onViewDetails={handleViewDetails}
              />
            ))}
        </Card>

        <CreateNewEntityModal
          open={isCreateModalOpen}
          onCancel={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateEntity}
        />

        <CommentsCard
          entityId={selectedEntityId}
          open={commentsOpen}
          onClose={handleCloseComments}
          showCard={false}
        />
      </Col>
    </Row>
  );
};

export default EntityParentLayout;
